import React, { useEffect, useMemo, useState } from 'react'
import { View, ScrollView, TouchableOpacity, ImageSourcePropType } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { TextInput, Button, Text, Icon, KeyboardAwareView, ScopeBadge } from '@components'
import { getAddGhostContactStyles } from '../styles'
import { SectionTitle } from './SectionTitle'
import { FormHashtagList } from '@components'
import { useForm } from 'react-hook-form'
import {
  ContactListItem,
  EncounterPlace,
  GenderType,
  getCommonStyles,
  getDayLabel,
  getFormatedList,
  getMomentLabel,
  GroupedItem,
  Scope,
  SelectionItem,
} from '@utils'
import { GenericSelectionModal } from '../../../../components/organisms/GenericSelectionModal'
import { AvailabilityModal, FormLocation } from '../../../../components/organisms'
import { FormDate } from '../../../../components/organisms'
import {
  useTranslatedContextStore,
  useTranslatedEmotionalContextStore,
  useTranslatedRelationshipStatusStore,
} from '@store'
import { TabSection } from './TabSection'
import {
  Profile,
  useExtension,
  useGetProfessionSectors,
  usePaginateCategories,
  usePaginateCities,
  usePaginateColorLabels,
  usePaginateCountries,
  usePaginateHashtags,
  usePaginateLanguages,
  usePaginateProfessions,
  useScreen,
  useSyncProfileRelationalColors,
  useUpdateProfileContexts,
  useUpdateProfileEncounterPlace,
} from '@hooks'
import { useLanguage } from '@context'
import { ProfileImage } from '../../../../screens/profile/components'

const AddGhostForm: React.FC<{
  formData: ContactListItem
  handleFormChange: (
    field: keyof ContactListItem,
    value: string | string[] | any | ImageSourcePropType | null,
  ) => void
  handleSave: (scope: Scope[]) => void
  profile?: Profile
}> = ({ formData, handleFormChange, handleSave, profile }) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()

  const { isMobile, isWeb, isDesktop } = useScreen()
  const styles = getAddGhostContactStyles(theme, isWeb)
  const commonStyles = getCommonStyles(theme)

  const { isExtension } = useExtension()
  const { control } = useForm<any>()

  const { contexts: availableEmotionalContexts } = useTranslatedEmotionalContextStore()
  const { statuses: availableRelationshipStatus } = useTranslatedRelationshipStatusStore()

  const [searchText, setSearchText] = useState('')
  const {
    flatData: colorLabels,
    isFetching: isFetchingColorLabels,
    isFetchingNextPage: isFetchingNextPageColorLabels,
    fetchNextPage: fetchNextPageColorLabels,
    hasNextPage: hasNextPageColorLabels,
  } = usePaginateColorLabels(searchText)
  const {
    flatData: countries,
    isFetching: isFetchingCountries,
    isFetchingNextPage: isFetchingNextPageCountries,
    fetchNextPage: fetchNextPageCountries,
    hasNextPage: hasNextPageCountries,
  } = usePaginateCountries({ search: searchText, lang: currentLanguage })
  const {
    flatData: cities,
    isFetching: isFetchingCities,
    isFetchingNextPage: isFetchingNextPageCities,
    fetchNextPage: fetchNextPageCities,
    hasNextPage: hasNextPageCities,
  } = usePaginateCities({ search: searchText, lang: currentLanguage })
  const {
    flatData: allLanguages,
    isFetching: isFetchingLanguages,
    isFetchingNextPage: isFetchingNextPageLanguages,
    fetchNextPage: fetchNextPageLanguages,
    hasNextPage: hasNextPageLanguages,
  } = usePaginateLanguages({ search: searchText, lang: currentLanguage })
  const {
    flatData: categories,
    isFetching: isFetchingCategories,
    isFetchingNextPage: isFetchingNextPageCategories,
    fetchNextPage: fetchNextPageCategories,
    hasNextPage: hasNextPageCategories,
  } = usePaginateCategories(searchText)
  const {
    flatData: hashtags,
    isFetching: isFetchingHashtags,
    isFetchingNextPage: isFetchingNextPageHashtags,
    fetchNextPage: fetchNextPageHashtags,
    hasNextPage: hasNextPageHashtags,
  } = usePaginateHashtags(searchText)

  const { contexts: groupedContexts } = useTranslatedContextStore()

  const { data: professionSectors = [] } = useGetProfessionSectors()
  const {
    flatData: professions,
    isFetching: isFetchingProfessions,
    isFetchingNextPage: isFetchingNextPageProfessions,
    fetchNextPage: fetchNextPageProfessions,
    hasNextPage: hasNextPageProfessions,
  } = usePaginateProfessions(searchText)

  // Group professions by sector
  const groupedProfessions: GroupedItem[] = useMemo(() => {
    if (!professionSectors.length || !professions.length) return []

    return professionSectors
      .map((sector) => ({
        id: sector.id,
        name: sector.name,
        items: professions
          .filter((prof) => prof.sectorId === sector.id)
          .map((prof) => ({
            id: prof.id,
            name: prof.name,
          })),
      }))
      .filter((group) => group.items.length > 0)
  }, [professionSectors, professions])

  const { mutate: updateProfileContexts } = useUpdateProfileContexts()
  const { mutate: updateEncounterPlace } = useUpdateProfileEncounterPlace()
  const { mutate: syncRelationalColors } = useSyncProfileRelationalColors()

  const [contexts, setContexts] = useState<SelectionItem[]>([])
  const [emotionalContexts, setEmotionalContexts] = useState<SelectionItem[]>([])
  const [relationshipStatus, setRelationshipStatus] = useState<SelectionItem[]>([])
  const [relationalColor, setRelationalColor] = useState<SelectionItem[]>([])
  const [initialRelationalColorIds, setInitialRelationalColorIds] = useState<string[]>([])
  const [encounterPlace, setEncounterPlace] = useState<EncounterPlace>()
  const [contextsDirty, setContextsDirty] = useState(false)

  useEffect(() => {
    if (profile?.meetingContextIds && Array.isArray(profile.meetingContextIds)) {
      const allMeeting = groupedContexts.flatMap((g) => g.items)
      const items = profile.meetingContextIds
        .map((id) => allMeeting.find((c) => c.id === id))
        .filter((c): c is SelectionItem => c != null)
      setContexts(items)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, profile?.meetingContextIds?.join(',')])

  useEffect(() => {
    if (profile?.emotionalContextIds && Array.isArray(profile.emotionalContextIds)) {
      const allEmotional = availableEmotionalContexts.flatMap((g) => g.items)
      const items = profile.emotionalContextIds
        .map((id) => allEmotional.find((c) => c.id === id))
        .filter((c): c is SelectionItem => c != null)
      setEmotionalContexts(items)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, profile?.emotionalContextIds?.join(',')])

  useEffect(() => {
    if (profile?.relationshipStatusId != null && profile.relationshipStatusId !== '') {
      const item = availableRelationshipStatus.find((r) => r.id === profile.relationshipStatusId)
      setRelationshipStatus(item ? [item] : [])
    } else {
      setRelationshipStatus([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id, profile?.relationshipStatusId])

  useEffect(() => {
    if (profile?.relationalColors !== undefined) {
      const list = (profile.relationalColors ?? []).map((r) => ({
        id: r.id,
        name: r.name,
        color: r.color,
      }))
      setRelationalColor(list)
      setInitialRelationalColorIds(list.map((r) => r.id))
    }
  }, [profile?.id, profile?.relationalColors])

  useEffect(() => {
    if (profile?.encounterPlace !== undefined) {
      const val =
        profile.encounterPlace && typeof profile.encounterPlace === 'object'
          ? {
              place: profile.encounterPlace.place ?? '',
              city: profile.encounterPlace.city,
              country: profile.encounterPlace.country,
              state: profile.encounterPlace.state,
              region: profile.encounterPlace.region,
            }
          : undefined
      setEncounterPlace(val)
    }
  }, [profile?.id, profile?.encounterPlace])
  const [isContextModalVisible, setIsContextModalVisible] = useState(false)
  const [isEmotionalContextModalVisible, setIsEmotionalContextModalVisible] = useState(false)
  const [isRelationshipStatusModalVisible, setIsRelationshipStatusModalVisible] = useState(false)
  const [isRelationalColorModalVisible, setIsRelationalColorModalVisible] = useState(false)

  const [isLanguagesModalVisible, setIsLanguagesModalVisible] = useState(false)
  const [isLivedPlacesModalVisible, setIsLivedPlacesModalVisible] = useState(false)
  const [isDisponibilityModalVisible, setIsDisponibilityModalVisible] = useState(false)
  const [isCategoriesModalVisible, setIsCategoriesModalVisible] = useState(false)
  const [isHashtagsModalVisible, setIsHashtagsModalVisible] = useState(false)
  const [isNationalityModalVisible, setIsNationalityModalVisible] = useState(false)
  const [isOriginModalVisible, setIsOriginModalVisible] = useState(false)
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false)
  const [isPositionModalVisible, setIsPositionModalVisible] = useState(false)
  const [scope, setScope] = useState(Scope.PRIVATE)

  const handleGenderSelect = (gender: GenderType) => {
    handleFormChange('gender', gender)
  }

  const handleAddHashtag = (key: string, value: string[]) => {
    handleFormChange(key as keyof ContactListItem, value)
  }

  const handleRemoveHashtag = (key: string, value: string[]) =>
    handleFormChange(key as keyof ContactListItem, value)

  const onScopeChange = (newScope: Scope) => {
    setScope(newScope)
  }

  const hasName = Boolean(formData.firstName?.trim() || formData.lastName?.trim())
  const isFormValid = hasName

  return (
    <>
      <KeyboardAwareView isFullScreen>
        <ScrollView style={styles.container}>
          <View style={[styles.form, isExtension && commonStyles.noPaddingTop]}>
            {isMobile && (
              <View style={[commonStyles.alignEnd, commonStyles.paddingBottom]}>
                <ScopeBadge scope={[scope]} onChange={onScopeChange} />
              </View>
            )}
            <View
              style={[
                { alignItems: 'center', marginBottom: theme.spacing.md },
                // Sit the photo on the left, above the "Info de contact" tab
                // (centered over a narrow region so it leans left, not middle).
                isDesktop && { width: 250 },
              ]}
            >
              <ProfileImage
                profileId={profile?.id}
                avatarUrl={profile?.avatarUrl || (formData.avatar as { uri?: string } | undefined)?.uri}
                isGhost={profile ? profile.isGhost : true}
                isMe={false}
                // Creation flow (no id yet): keep the chosen photo on the form
                // so it's sent when the ghost profile is saved.
                onImageSelect={
                  !profile?.id ? (uri) => handleFormChange('avatar', { uri }) : undefined
                }
              />
            </View>
            <TabSection
              rightAccessory={
                !isMobile ? <ScopeBadge scope={[scope]} onChange={onScopeChange} /> : undefined
              }
              firstTab={
                <>
                  <View style={[styles.genderContainer, !isMobile && { alignSelf: 'flex-start' }]}>
                    <TouchableOpacity
                      style={[
                        styles.genderButton,
                        formData.gender === 'female' && styles.genderButtonSelected,
                      ]}
                      onPress={() => handleGenderSelect('female')}
                    >
                      <Text
                        type="bodySmall"
                        color={
                          formData.gender === 'female' ? theme.colors.white : theme.colors.text
                        }
                      >
                        {t('form.woman')}
                      </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[
                        styles.genderButton,
                        formData.gender === 'male' && styles.genderButtonSelected,
                      ]}
                      onPress={() => handleGenderSelect('male')}
                    >
                      <Text
                        type="bodySmall"
                        color={formData.gender === 'male' ? theme.colors.white : theme.colors.text}
                      >
                        {t('form.man')}
                      </Text>
                    </TouchableOpacity>

                    {/*<TouchableOpacity
                      style={[
                        styles.genderButton,
                        formData.gender === 'non-binary' && styles.genderButtonSelected
                      ]}
                      onPress={() => handleGenderSelect('non-binary')}
                    >
                      <Text
                        type="body"
                        color={formData.gender === 'non-binary' ? theme.colors.white : theme.colors.text}
                      >
                        Non binaire
                      </Text>
                    </TouchableOpacity>*/}
                  </View>
                  <View
                    style={
                      isMobile
                        ? commonStyles.flex
                        : [commonStyles.rowGapLg, { alignItems: 'flex-start' }]
                    }
                  >
                    <View style={isDesktop && [styles.fixedWidth, styles.marginRight]}>
                      {/* Section Identité */}
                      <SectionTitle title={t('form.mainInfo')}>
                        <TextInput
                          title={t('form.firstName')}
                          value={formData.firstName}
                          onChangeText={(text) => handleFormChange('firstName', text)}
                          placeholder={t('form.enterFirstName')}
                          containerStyle={styles.input}
                          iconRight="userPlus"
                          hasError={!hasName}
                        />

                        <TextInput
                          title={t('form.lastName')}
                          value={formData.lastName}
                          onChangeText={(text) => handleFormChange('lastName', text)}
                          placeholder={t('form.enterLastName')}
                          containerStyle={styles.input}
                          iconRight="userPlus"
                          hasError={!hasName}
                        />

                        <TouchableOpacity onPress={() => setIsPositionModalVisible(true)}>
                          <TextInput
                            title={t('form.professions')}
                            value={getFormatedList(
                              formData.professions?.map((profession) => profession.name) || [],
                            )}
                            placeholder={t('form.enterProfession')}
                            containerStyle={styles.input}
                            iconRight="work"
                            editable={false}
                            pointerEvents="none"
                          />
                        </TouchableOpacity>
                        <TextInput
                          title={t('form.company')}
                          value={formData.company}
                          onChangeText={(text) => handleFormChange('company', text)}
                          placeholder={t('form.enterCompany')}
                          containerStyle={styles.input}
                          iconRight="work"
                        />
                        <TextInput
                          title={t('form.email')}
                          value={formData.email}
                          onChangeText={(text) => handleFormChange('email', text)}
                          placeholder={t('form.email')}
                          containerStyle={styles.input}
                          keyboardType="email-address"
                          iconRight="message"
                        />

                        <TextInput
                          title={t('form.phoneNumber')}
                          value={formData.phone}
                          onChangeText={(text) => handleFormChange('phone', text)}
                          placeholder={t('form.phoneNumber')}
                          containerStyle={styles.input}
                          keyboardType="phone-pad"
                          iconRight="phone"
                        />
                        {/*<TextInput
                          title={"Notes :"}
                          containerStyle={styles.input}
                          value={formData.notes}
                          onChangeText={(text) => handleFormChange('notes', text)}
                          placeholder="Notes"
                          multiline
                          numberOfLines={4}
                          iconRight="note"
                        />*/}
                      </SectionTitle>
                    </View>
                    <View style={isDesktop && styles.fixedWidth}>
                      {/* Section Contacts */}
                      <SectionTitle title={t('form.socialNetworks')}>
                        <TextInput
                          value={formData.facebook}
                          onChangeText={(text) => handleFormChange('facebook', text)}
                          placeholder={t('ghostForm.facebook')}
                          containerStyle={styles.input}
                          iconRight="facebook"
                          iconColor={theme.colors.primary}
                        />

                        <TextInput
                          value={formData.instagram}
                          onChangeText={(text) => handleFormChange('instagram', text)}
                          placeholder={t('ghostForm.instagram')}
                          containerStyle={styles.input}
                          iconRight="instagram"
                          iconColor={theme.colors.primary}
                        />

                        <TextInput
                          value={formData.linkedin}
                          onChangeText={(text) => handleFormChange('linkedin', text)}
                          placeholder={t('ghostForm.linkedin')}
                          containerStyle={styles.input}
                          iconRight="linkedin"
                          iconColor={theme.colors.primary}
                        />

                        <TextInput
                          value={formData.twitter}
                          onChangeText={(text) => handleFormChange('twitter', text)}
                          placeholder={t('ghostForm.twitter')}
                          containerStyle={styles.input}
                          iconRight="twitter"
                          iconColor={theme.colors.primary}
                        />
                        <TextInput
                          title={t('form.website')}
                          value={formData.website}
                          onChangeText={(text) => handleFormChange('website', text)}
                          placeholder={t('form.website')}
                          containerStyle={styles.input}
                          keyboardType="url"
                          iconRight="globe"
                        />
                      </SectionTitle>
                    </View>
                  </View>
                </>
              }
              secondTab={
                <>
                  <View
                    style={
                      isMobile
                        ? commonStyles.flex
                        : [commonStyles.rowGapLg, { alignItems: 'flex-start' }]
                    }
                  >
                    <View style={isDesktop && [styles.fixedWidth, styles.marginRight]}>
                      <SectionTitle title={t('form.presentation')}>
                        <TextInput
                          title={t('ghostProfile.about')}
                          iconRight="edit"
                          containerStyle={styles.input}
                          value={formData.about}
                          onChangeText={(text) => handleFormChange('about', text)}
                          placeholder={t('form.descriptionPlaceholder')}
                          isMultiline
                          scrollEnabled={false}
                        />
                        <FormDate
                          control={control}
                          name="birthDate"
                          title={t('form.birthDate')}
                          icon="calendar"
                          placeholder="JJ-MM-AAAA"
                          value={formData.birthDate}
                          onChange={(date) => handleFormChange('birthDate', date)}
                        />
                        <TouchableOpacity
                          onPress={() => setIsAddressModalVisible(true)}
                          style={commonStyles.paddingTop}
                        >
                          <TextInput
                            title={t('form.livingPlace')}
                            value={formData.residence?.name}
                            onChangeText={(text) => handleFormChange('residence', text)}
                            placeholder={t('form.livingPlace')}
                            containerStyle={styles.input}
                            iconRight="globe"
                            editable={false}
                            pointerEvents="none"
                          />
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => setIsOriginModalVisible(true)}>
                          <TextInput
                            title={t('form.origins')}
                            value={(formData.origins || []).map((origin) => origin.name).join(', ')}
                            onChangeText={(text) => handleFormChange('origins', text)}
                            placeholder={t('form.origins')}
                            containerStyle={styles.input}
                            iconRight="globe"
                            editable={false}
                            pointerEvents="none"
                          />
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => setIsNationalityModalVisible(true)}>
                          <TextInput
                            title={t('form.nationalities')}
                            value={formData.nationalities
                              ?.map((nationality) => nationality.name)
                              .join(', ')}
                            onChangeText={(text) => handleFormChange('nationalities', text)}
                            placeholder={t('form.nationalityPlaceholder')}
                            containerStyle={styles.input}
                            iconRight="globe"
                            editable={false}
                            pointerEvents="none"
                          />
                        </TouchableOpacity>

                        <TouchableOpacity onPress={() => setIsLanguagesModalVisible(true)}>
                          <TextInput
                            title={t('form.spokenLanguages')}
                            value={getFormatedList(
                              formData.languages?.map((language) => language.name) || [],
                            )}
                            onChangeText={(text) => handleFormChange('languages', text)}
                            placeholder={t('form.enterLanguages')}
                            containerStyle={styles.input}
                            iconRight="translate"
                            editable={false}
                            pointerEvents="none"
                          />
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => setIsLivedPlacesModalVisible(true)}>
                          <TextInput
                            title={t('form.livedPlaces')}
                            value={getFormatedList(
                              formData.residenceHistory?.map((place) => place.name) || [],
                            )}
                            onChangeText={(text) => handleFormChange('residenceHistory', text)}
                            placeholder={t('form.livedPlaces')}
                            containerStyle={styles.input}
                            iconRight="mapPin"
                            editable={false}
                            pointerEvents="none"
                          />
                        </TouchableOpacity>
                      </SectionTitle>
                    </View>
                    <View style={isDesktop && styles.fixedWidth}>
                      <SectionTitle title={t('form.availabilityAndMeetings')}>
                        <View style={styles.disponibilityBackground}>
                          <Icon
                            name="edit"
                            color={theme.colors.greyBlue}
                            size={theme.iconSize.medium}
                            containerStyle={styles.editIcon}
                          />
                          <TouchableOpacity onPress={() => setIsDisponibilityModalVisible(true)}>
                            <Text
                              type="bodySmall"
                              color={theme.colors.lightGrey}
                              style={styles.title}
                            >
                              {t('form.availability')}
                            </Text>
                            {formData.disponibilities && formData.disponibilities.length > 0 && (
                              <View style={styles.disponibilityContainer}>
                                <View style={{ marginTop: theme.spacing.sm }}>
                                  {formData.disponibilities.map((d, index) => (
                                    <View key={index} style={styles.disponibilityItem}>
                                      <Text type="body">
                                        {getDayLabel(t, d.dayStart?.id) || d.dayStart?.name || ''} -{' '}
                                        {getDayLabel(t, d.dayEnd?.id) || d.dayEnd?.name || ''}{' '}
                                        {d.timeStart} - {d.timeEnd}
                                      </Text>
                                    </View>
                                  ))}
                                </View>
                              </View>
                            )}
                          </TouchableOpacity>
                          {formData.bestMoments && formData.bestMoments.length > 0 ? (
                            <View style={styles.bestMomentsSection}>
                              <Text
                                type="bodySmall"
                                color={theme.colors.lightGrey}
                                style={styles.bestMomentsTitle}
                              >
                                {t('form.bestMoment')}
                              </Text>

                              <ScrollView
                                horizontal
                                showsHorizontalScrollIndicator={false}
                                contentContainerStyle={styles.bestMomentsScrollContent}
                                style={styles.bestMomentsScroll}
                              >
                                {formData.bestMoments?.map((moment, index) => (
                                  <View key={index} style={styles.bestMomentItem}>
                                    <Icon
                                      name={moment.code}
                                      color={theme.colors.primary}
                                      size={theme.iconSize.large}
                                    />
                                    <Text
                                      type="bodySmall"
                                      color={theme.colors.primary}
                                      style={styles.bestMomentLabel}
                                    >
                                      {getMomentLabel(
                                        t,
                                        moment.code,
                                        moment.nameFr,
                                        moment.nameEn,
                                        currentLanguage,
                                        moment.nameEs,
                                      )}
                                    </Text>
                                  </View>
                                ))}
                              </ScrollView>
                            </View>
                          ) : (
                            <></>
                          )}
                        </View>
                        {formData.id ? (
                          <>
                            <TouchableOpacity onPress={() => setIsContextModalVisible(true)}>
                              <TextInput
                                title={t('contextes.meetingContext')}
                                value={getFormatedList(contexts.map((c) => c.name))}
                                placeholder={t('contextes.chooseMeetingContext')}
                                containerStyle={styles.input}
                                iconRight="work"
                                editable={false}
                                pointerEvents="none"
                              />
                            </TouchableOpacity>
                            <TouchableOpacity
                              onPress={() => setIsEmotionalContextModalVisible(true)}
                            >
                              <TextInput
                                title={t('contextes.associatedEmotion')}
                                value={getFormatedList(emotionalContexts.map((c) => c.name))}
                                placeholder={t('contextes.associatedEmotion')}
                                containerStyle={styles.input}
                                iconRight="heart"
                                editable={false}
                                pointerEvents="none"
                              />
                            </TouchableOpacity>
                            <TouchableOpacity
                              onPress={() => setIsRelationshipStatusModalVisible(true)}
                            >
                              <TextInput
                                title={t('contextes.relationshipEvolution')}
                                value={relationshipStatus?.[0]?.name}
                                placeholder={t('contextes.chooseRelationship')}
                                containerStyle={styles.input}
                                iconRight="people"
                                editable={false}
                                pointerEvents="none"
                              />
                            </TouchableOpacity>
                            <TouchableOpacity
                              onPress={() => setIsRelationalColorModalVisible(true)}
                            >
                              <TextInput
                                title={t('contextes.associatedColor')}
                                value={relationalColor?.[0]?.name}
                                placeholder={t('contextes.associatedColor')}
                                containerStyle={styles.input}
                                iconRight="link2"
                                editable={false}
                                pointerEvents="none"
                              />
                            </TouchableOpacity>
                            <FormLocation
                              title={t('contextes.referencePlace')}
                              value={encounterPlace?.place || ''}
                              onChange={(val: EncounterPlace) => {
                                setEncounterPlace(val)
                                setContextsDirty(true)
                              }}
                              placeholder={t('contextes.enterPlace')}
                              iconRight="mapPin"
                            />
                          </>
                        ) : (
                          <></>
                        )}
                      </SectionTitle>

                      {/* Section Tags */}
                      <SectionTitle title={t('ghostForm.tagsSection')} collapsible>
                        <FormHashtagList
                          control={control}
                          name={t('ghostForm.profileHashtagsName')}
                          placeholder={t('ghostForm.hashtagPlaceholder')}
                          values={formData.hashtags || []}
                          isGhost
                          iconRight="htag"
                          addItem={(value) => handleAddHashtag('hashtags', value)}
                          removeItem={(value) => handleRemoveHashtag('hashtags', value)}
                        />
                        <FormHashtagList
                          control={control}
                          name="Objectifs"
                          placeholder={t('ghostForm.goalPlaceholder')}
                          values={formData.goals || []}
                          isGhost
                          isSentence
                          iconRight="translate"
                          addItem={(value) => handleAddHashtag('goals', value)}
                          removeItem={(value) => handleRemoveHashtag('goals', value)}
                        />
                        <FormHashtagList
                          control={control}
                          name="Hobbies"
                          placeholder="#faire_du_karate..."
                          values={formData.hobbies || []}
                          isGhost
                          isSingleTag
                          iconRight="heart"
                          addItem={(value) => handleAddHashtag('hobbies', value)}
                          removeItem={(value) => handleRemoveHashtag('hobbies', value)}
                        />
                        <FormHashtagList
                          control={control}
                          name="Compétences"
                          placeholder="#marketing..."
                          values={formData.skills || []}
                          isGhost
                          isSingleTag
                          iconRight="heart"
                          addItem={(value) => handleAddHashtag('skills', value)}
                          removeItem={(value) => handleRemoveHashtag('skills', value)}
                        />
                      </SectionTitle>
                      <SectionTitle
                        title={t('ghostForm.pinsSection')}
                        subtitle={t('ghostForm.organizeProfile')}
                        collapsible
                      >
                        <TouchableOpacity onPress={() => setIsCategoriesModalVisible(true)}>
                          <TextInput
                            title={t('categoriesScreen.title')}
                            value={getFormatedList(
                              formData.pinsCategories?.map((category) => category.name) || [],
                            )}
                            onChangeText={(text) => handleFormChange('pinsCategories', text)}
                            placeholder={t('categoriesScreen.title')}
                            containerStyle={styles.input}
                            iconRight="category"
                            editable={false}
                            pointerEvents="none"
                          />
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => setIsHashtagsModalVisible(true)}>
                          <TextInput
                            title={t('form.hashtags')}
                            value={getFormatedList(formData.pinsHashtags?.map((h) => h.name) || [])}
                            onChangeText={(text) => handleFormChange('hashtags', text)}
                            placeholder={t('form.hashtags')}
                            containerStyle={styles.input}
                            iconRight="htag"
                            editable={false}
                            pointerEvents="none"
                          />
                        </TouchableOpacity>
                      </SectionTitle>
                    </View>
                  </View>
                </>
              }
            />
          </View>
        </ScrollView>
      </KeyboardAwareView>
      <View
        style={
          isExtension || isMobile
            ? [commonStyles.paddingHorizontal, commonStyles.paddingBottom]
            : styles.footer
        }
      >
        <Button
          title={t('common.save')}
          onPress={() => {
            handleSave([scope, Scope.GHOST])
            if (formData.id && contextsDirty) {
              updateProfileContexts({
                profileId: formData.id,
                meetingContextIds: contexts.map((c) => c.id),
                emotionalContextIds: emotionalContexts.map((c) => c.id),
                relationshipStatusId: relationshipStatus[0]?.id ?? null,
              })
              updateEncounterPlace({
                profileId: formData.id,
                encounterPlace: encounterPlace ?? null,
              })
              if (relationalColor.length > 0 || initialRelationalColorIds.length > 0) {
                syncRelationalColors({
                  profileId: formData.id,
                  previousIds: initialRelationalColorIds,
                  nextIds: relationalColor.map((c) => c.id),
                })
              }
            }
          }}
          unfilled={false}
          isSmall={!isExtension && !isMobile}
          disabled={!isFormValid}
        />
      </View>
      <GenericSelectionModal
        isVisible={isLanguagesModalVisible}
        setIsVisible={setIsLanguagesModalVisible}
        title={t('form.spokenLanguages')}
        placeholder={t('form.searchLanguage')}
        availableItems={allLanguages.map((language) => ({
          id: language.code,
          name: language.name ?? language.nameFr,
        }))}
        selectedItems={formData.languages || []}
        onSelectionChange={(items) => {
          handleFormChange('languages', items)
        }}
        isFetching={isFetchingLanguages}
        fetchNextPage={() => {
          fetchNextPageLanguages()
        }}
        hasNextPage={hasNextPageLanguages}
        isFetchingNextPage={isFetchingNextPageLanguages}
        onSearch={(text) => setSearchText(text)}
      />

      <GenericSelectionModal
        isVisible={isOriginModalVisible || isAddressModalVisible}
        setIsVisible={(val) => {
          if (isOriginModalVisible) {
            setIsOriginModalVisible(val)
          } else if (isAddressModalVisible) {
            setIsAddressModalVisible(val)
          }
        }}
        title={isOriginModalVisible ? t('form.origins') : t('form.livingPlace')}
        placeholder={t('form.searchPlace')}
        availableItems={
          isAddressModalVisible
            ? cities.map((city) => ({
                id: city.id,
                name: city.name ?? city.nameFr,
              }))
            : countries.map((country) => ({
                id: country.code,
                name: country.name ?? country.nameFr,
              }))
        }
        selectedItems={(isOriginModalVisible
          ? ((formData.origins || []) as SelectionItem[])
          : [formData.residence as SelectionItem]
        ).filter(Boolean)}
        onSelectionChange={(items) => {
          if (items.length === 0) return
          if (isOriginModalVisible) {
            handleFormChange('origins', items)
          } else if (isAddressModalVisible) {
            handleFormChange('residence', items[0])
          }
        }}
        multiSelect={isOriginModalVisible}
        isFetching={isFetchingCities || isFetchingCountries}
        fetchNextPage={() => {
          if (isAddressModalVisible) {
            fetchNextPageCities()
          } else if (isOriginModalVisible) {
            fetchNextPageCountries()
          }
        }}
        hasNextPage={
          isAddressModalVisible
            ? hasNextPageCities
            : isOriginModalVisible
              ? hasNextPageCountries
              : false
        }
        isFetchingNextPage={isFetchingNextPageCities || isFetchingNextPageCountries}
        onSearch={(text) => setSearchText(text)}
      />

      <GenericSelectionModal
        isVisible={isNationalityModalVisible}
        setIsVisible={setIsNationalityModalVisible}
        title={t('form.nationalities')}
        placeholder={t('form.searchNationality')}
        availableItems={countries.map((country) => ({
          id: country.code,
          name: country.name ?? country.nameFr,
        }))}
        selectedItems={formData.nationalities as SelectionItem[]}
        onSelectionChange={(items) => {
          if (items.length === 0) return
          handleFormChange('nationalities', items)
        }}
        isFetching={isFetchingCountries}
        fetchNextPage={() => {
          fetchNextPageCountries()
        }}
        hasNextPage={hasNextPageCountries}
        isFetchingNextPage={isFetchingNextPageCountries}
        onSearch={(text) => setSearchText(text)}
      />

      <GenericSelectionModal
        isVisible={isHashtagsModalVisible}
        setIsVisible={setIsHashtagsModalVisible}
        title={t('form.hashtags')}
        placeholder={t('form.searchHashtags')}
        availableItems={hashtags}
        selectedItems={formData.pinsHashtags || []}
        isFetching={isFetchingHashtags}
        fetchNextPage={() => {
          fetchNextPageHashtags()
        }}
        hasNextPage={hasNextPageHashtags}
        isFetchingNextPage={isFetchingNextPageHashtags}
        onSearch={(text) => setSearchText(text)}
        onSelectionChange={(items) => {
          handleFormChange('pinsHashtags', items)
        }}
        isHashtagsModal
      />
      <GenericSelectionModal
        isVisible={isCategoriesModalVisible}
        setIsVisible={setIsCategoriesModalVisible}
        title={t('form.searchCategories')}
        placeholder={t('form.searchCategories')}
        availableItems={categories.map((category) => ({
          id: category.id,
          name: category.name,
          photo: category.avatarUrl ?? '',
        }))}
        selectedItems={formData.pinsCategories || []}
        onSelectionChange={(items) => {
          handleFormChange('pinsCategories', items)
        }}
        isCategoriesModal
        isFetching={isFetchingCategories}
        fetchNextPage={() => {
          fetchNextPageCategories()
        }}
        hasNextPage={hasNextPageCategories}
        isFetchingNextPage={isFetchingNextPageCategories}
        onSearch={(text) => setSearchText(text)}
      />
      <GenericSelectionModal
        isVisible={isLivedPlacesModalVisible}
        setIsVisible={setIsLivedPlacesModalVisible}
        title={t('form.livedPlaces')}
        placeholder={t('form.searchPlace')}
        availableItems={cities.map((city) => ({
          id: city.id,
          name: city.name ?? city.nameFr,
        }))}
        selectedItems={formData.residenceHistory || []}
        onSelectionChange={(items) => {
          handleFormChange('residenceHistory', items)
        }}
        isFetching={isFetchingCities}
        fetchNextPage={() => {
          fetchNextPageCities()
        }}
        hasNextPage={hasNextPageCities}
        isFetchingNextPage={isFetchingNextPageCities}
        onSearch={(text) => setSearchText(text)}
      />

      <GenericSelectionModal
        isVisible={isContextModalVisible}
        setIsVisible={setIsContextModalVisible}
        title={t('contextes.meetingContext')}
        placeholder={t('contextes.searchMeetingContext')}
        availableItems={groupedContexts}
        selectedItems={contexts}
        multiSelect={true}
        onSelectionChange={(items) => {
          setContexts(items)
          setContextsDirty(true)
          handleFormChange('encounterContext', items.length > 0 ? items[0] : null)
        }}
      />

      <GenericSelectionModal
        isVisible={isEmotionalContextModalVisible}
        setIsVisible={setIsEmotionalContextModalVisible}
        title={t('contextes.associatedEmotion')}
        placeholder={t('contextes.searchEmotion')}
        availableItems={availableEmotionalContexts}
        selectedItems={emotionalContexts}
        multiSelect={true}
        onSelectionChange={(emotionalContexts) => {
          setEmotionalContexts(emotionalContexts)
          setContextsDirty(true)
        }}
      />

      <GenericSelectionModal
        isVisible={isRelationshipStatusModalVisible}
        setIsVisible={setIsRelationshipStatusModalVisible}
        title={t('contextes.relationshipEvolution')}
        placeholder={t('contextes.searchRelationship')}
        availableItems={availableRelationshipStatus}
        selectedItems={relationshipStatus}
        multiSelect={false}
        onSelectionChange={(relationshipStatus) => {
          setRelationshipStatus(relationshipStatus)
          setContextsDirty(true)
        }}
      />

      <GenericSelectionModal
        isVisible={isRelationalColorModalVisible}
        setIsVisible={setIsRelationalColorModalVisible}
        title={t('contextes.associatedColor')}
        placeholder={t('contextes.searchColor')}
        availableItems={colorLabels.map((colorLabel) => ({
          id: colorLabel.id,
          name: colorLabel.name,
          color: colorLabel.color,
        }))}
        selectedItems={relationalColor}
        multiSelect={false}
        onSelectionChange={(relationalColor) => {
          setRelationalColor(relationalColor)
          setContextsDirty(true)
        }}
        isFetching={isFetchingColorLabels}
        fetchNextPage={fetchNextPageColorLabels}
        hasNextPage={hasNextPageColorLabels}
        isFetchingNextPage={isFetchingNextPageColorLabels}
        onSearch={(text) => setSearchText(text)}
      />

      <GenericSelectionModal
        isVisible={isPositionModalVisible}
        setIsVisible={setIsPositionModalVisible}
        title={t('form.professions')}
        placeholder={t('form.searchProfession')}
        availableItems={groupedProfessions}
        selectedItems={formData.professions || []}
        multiSelect={true}
        isFetching={isFetchingProfessions}
        isFetchingNextPage={isFetchingNextPageProfessions}
        hasNextPage={hasNextPageProfessions}
        fetchNextPage={fetchNextPageProfessions}
        onSearch={(search) => {
          setSearchText(search)
        }}
        onSelectionChange={(items) => {
          handleFormChange('professions', items)
        }}
        fitParent={isMobile}
      />

      <AvailabilityModal
        isVisible={isDisponibilityModalVisible}
        setIsVisible={setIsDisponibilityModalVisible}
        disponibilities={formData.disponibilities || []}
        setDisponibilities={(disponibilities) =>
          handleFormChange('disponibilities', disponibilities)
        }
        bestMoments={formData.bestMoments || []}
        setBestMoments={(bestMoments) => handleFormChange('bestMoments', bestMoments)}
      />
    </>
  )
}

export default AddGhostForm
